import PropTypes from "prop-types"
import React, { Fragment, useEffect } from "react"
import Modal from "react-bootstrap/Modal"
import Typography from "@mui/material/Typography"
import { useSelector } from "react-redux"
import { APP_MODAL_TYPES } from "../../../utilities/constants"
import AttachmentModal from "./attachment-modal"
import ErrorModal from "./error-modal"
import FormModal from "./form-modal"
import GameplayHelperModal from "./gameplay-helper-modal"
import InfoModal from "./info-modal"
import "./modal.scss"
import SuccessModal from "./success-modal"

const getModalComponent = (modalType, modalData) => {
  if (modalType === APP_MODAL_TYPES.ERROR) {
    return React.createElement(ErrorModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
    })
  } else if (modalType === APP_MODAL_TYPES.INFO) {
    return React.createElement(InfoModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
    })
  } else if (modalType === APP_MODAL_TYPES.SUCCESS) {
    return React.createElement(SuccessModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
    })
  } else if (modalType === APP_MODAL_TYPES.ATTACHMENT) {
    return React.createElement(AttachmentModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
      attachments: modalData.attachments,
    })
  } else if (modalType === APP_MODAL_TYPES.FORM) {
    return React.createElement(FormModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
      formData: modalData.formData,
      formCallback: modalData.formCallback,
    })
  } else if (modalType === APP_MODAL_TYPES.GAMEPLAY_HELP_SCREENS) {
    return React.createElement(GameplayHelperModal, {
      title: modalData.title,
      subTitle: modalData.subTitle,
      actionButtons: modalData.actionButtons,
      isFirstHelperModal: modalData.isFirstHelperModal,
    })
  } else if (modalType === APP_MODAL_TYPES.COMPONENT) {
    return React.createElement(modalData.component, {
      ...modalData.componentInputs,
    })
  } else {
    return null
  }
}

const modalSizeForType = (modalType) => {
  if (modalType === APP_MODAL_TYPES.ATTACHMENT) {
    return "xl"
  } else if (modalType === APP_MODAL_TYPES.FORM) {
    return "lg"
  }
  return undefined
}

const getModalBackdrop = (modalType) => {
  switch (modalType) {
    case APP_MODAL_TYPES.GAMEPLAY_HELP_SCREENS:
      return "static"
    default:
      return false
  }
}

const shouldModalCloseOnKeyboardEscape = (modalType) => {
  return ![APP_MODAL_TYPES.GAMEPLAY_HELP_SCREENS].includes(modalType)
}

const shouldCloseButtonHide = (modalType) => {
  return [APP_MODAL_TYPES.GAMEPLAY_HELP_SCREENS].includes(modalType)
}

const updateModalAdditionalStyle = (modalType, modalBodyClass) => {
  switch (modalType) {
    case APP_MODAL_TYPES.GAMEPLAY_HELP_SCREENS: {
      const modalElement = document.querySelector(`.${modalBodyClass[0]}`)
      const { width: modalContentWidth, height: modalContentHeight } = document
        .querySelector(`.${modalBodyClass[0]} .modal-content`)
        .getBoundingClientRect()
      if (modalElement) {
        let style = {
          display: "block",
          margin: "0px",
          transform: `translate3d(-${modalContentWidth + 20}px, -${modalContentHeight / 2 - 40}px, 0)`,
        }
        const activeHelpStep = String(modalBodyClass.find((cName) => cName.includes("step-"))).split("-")[1]
        if (activeHelpStep === "0") {
          style = {}
        } else if (activeHelpStep === "1") {
          const element = document.querySelector(`.title-container .user-action-wrapper`)
          const { top: elementTop, left: elementLeft } = element.getBoundingClientRect()
          style.top = `${elementTop}px`
          style.left = `${elementLeft}px`
        } else if (activeHelpStep === "2") {
          const element = document.querySelector(`.option-container .option-wrapper`)
          const { top: elementTop, left: elementLeft } = element.getBoundingClientRect()
          style.top = `${elementTop}px`
          style.left = `${elementLeft}px`
        }
        const styleString = Object.entries(style)
          .map(([k, v]) => `${k}:${v}`)
          .join(";")
        modalElement.setAttribute("style", styleString)
      }
      break
    }
    default:
      break
  }
}

function AppModal({ modalType, modalData, modalBodyClass, handleModalClose }) {
  const showModal = useSelector((state) => state.ui.showModal)
  const modalComponent = getModalComponent(modalType, modalData)
  const modalSize = modalSizeForType(modalType)
  const backdrop = getModalBackdrop(modalType)
  const keyboard = shouldModalCloseOnKeyboardEscape(modalType)
  const hideCloseButton = shouldCloseButtonHide(modalType)

  useEffect(() => {
    updateModalAdditionalStyle(modalType, modalBodyClass)
  }, [modalType, modalBodyClass])

  // const modalDialogClassName = modalDialogClassNameForType(modalType)

  return (
    <Fragment>
      <Modal
        dialogClassName={modalBodyClass?.join(" ")}
        show={showModal}
        onHide={handleModalClose}
        centered
        size={modalSize}
        backdrop={backdrop}
        keyboard={keyboard}
      >
        <span type="button" className={`close-btn ${hideCloseButton ? "d-none" : ""}`} aria-label="Close" onClick={handleModalClose}>
          <img src="images/circle_cross_grey.png" alt="Close Icon" width={25} height={25} />
        </span>
        <Modal.Body>
          {modalComponent}
          {/* KPI P2 §8.6: an optional caption under the body (the rating card's results notice). */}
          {modalData?.footNote ? (
            <Typography
              variant="caption"
              component="p"
              className="app-modal-footnote"
              sx={{ mt: 2, mb: 0, color: "text.secondary", textAlign: "center" }}
            >
              {modalData.footNote}
            </Typography>
          ) : null}
        </Modal.Body>
      </Modal>
    </Fragment>
  )
}

AppModal.propTypes = {
  modalType: PropTypes.string.isRequired,
  modalData: PropTypes.exact({
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
    actionButtons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        classNames: PropTypes.array,
        clickHandler: PropTypes.func,
      })
    ).isRequired,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        src: PropTypes.string,
        alt: PropTypes.string,
      })
    ),
    formData: PropTypes.object,
    formCallback: PropTypes.func,
    isFirstHelperModal: PropTypes.bool,
    footNote: PropTypes.string,
  }),
  modalBodyClass: PropTypes.arrayOf(PropTypes.string),
  handleModalClose: PropTypes.func.isRequired,
}

export default AppModal
